import {
  ApiError,
  type BookingResult,
  type BusinessProfile,
  type Groomer,
  type GroomingCatalog,
  type GroomingSlot,
  type IdentityStartResponse,
  type IdentityVerifyResponse,
  type MultiBookingResult,
  type PricingPreview,
  type RoomCard,
} from './types'
import { mock } from './mock'

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'
const BASE = (import.meta.env.VITE_API_BASE || '') + '/api/v1/book'

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let resp: Response
  try {
    resp = await fetch(`${BASE}${path}`, {
      headers: { 'Content-Type': 'application/json' },
      ...init,
    })
  } catch {
    throw new ApiError(0, 'errors.generic')
  }
  if (!resp.ok) {
    let detail = ''
    try {
      const body = (await resp.json()) as { detail?: string }
      detail = body?.detail ?? ''
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(resp.status, detail || 'errors.generic')
  }
  if (resp.status === 204) return undefined as T
  return (await resp.json()) as T
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') sp.set(k, v)
  const s = sp.toString()
  return s ? `?${s}` : ''
}

// ADDONS-4 — one selected add-on line in a create body. price_minor is
// echoed (tax-inclusive) so the backend can 409 if it changed since the
// SPA fetched the catalog.
export interface AddOnLine {
  id: string
  quantity: number
  price_minor: number
}

export interface BoardingCreateBody {
  booking_token: string
  guest_id?: string
  pet_name?: string
  species?: string
  breed?: string
  pet_notes?: string
  room_id?: string
  service_type: string
  check_in_date: string
  check_out_date: string
  size_tier?: string
  special_instructions?: string
  package_purchase_id?: string  // C3 — redeem from existing pack at booking time
  add_ons?: AddOnLine[]
}

export interface GroomingCreateBody {
  booking_token: string
  guest_id?: string
  pet_name?: string
  species?: string
  breed?: string
  service_id?: string
  package_id?: string
  groomer_id?: string
  scheduled_date: string
  scheduled_start: string // ISO datetime
  scheduled_end: string
  size_tier?: string
  coat_type?: string
  special_requests?: string
  package_purchase_id?: string  // C3 — redeem from existing pack
  add_ons?: AddOnLine[]
}

// C3 — Service-package offering shapes (browse + buy + redeem)

export interface PackageTierOffering {
  id: string
  paid_sessions: number
  bonus_sessions: number
  price_minor: number
  label_en: string | null
  label_zh: string | null
  is_default: boolean
}

export interface ServicePackageOffering {
  id: string
  service_type: 'boarding' | 'daycare' | 'grooming'
  name_en: string
  name_zh: string
  description_en: string | null
  description_zh: string | null
  currency: string
  validity_days: number | null
  transferability: 'household' | 'owner_only'
  refund_policy_mode: 'none' | 'prorated' | 'prorated_with_fee' | 'full_within_window'
  einvoice_required: boolean
  tiers: PackageTierOffering[]
}

export interface AddOnOffering {
  id: string
  name: string       // English fallback
  name_zh: string    // consumer-facing primary
  category: string
  price_minor: number  // tax-inclusive
  duration_minutes: number | null
}

export interface MyPackagePublic {
  id: string
  package_id: string
  name_en: string
  name_zh: string
  service_type: 'boarding' | 'daycare' | 'grooming'
  sessions_total: number
  sessions_remaining: number
  expires_at: string | null
  transferability: 'household' | 'owner_only'
  status: string
}

export interface PackageCheckoutBody {
  booking_token: string
  package_id: string
  tier_id: string
  success_url: string
  cancel_url: string
  consent_accepted: boolean
  einvoice_carrier?: string
  einvoice_tax_id?: string
  /**
   * transferability='owner_only' packages are redeemable only by the pet
   * named at purchase, so exactly one of these is required for them:
   * pet_id to pin an existing pet, or pet_name to create one (first-time
   * buyer with no pets on file). Both are ignored for 'household'.
   */
  pet_id?: string
  pet_name?: string
  species?: string
}

/** A pet on file for the owner behind the booking_token. */
export interface MyPetPublic {
  id: string
  pet_name: string
  species: string | null
  breed: string | null
}

/** Stripe → redirect URL; ECPay → auto-submit POST form to the hosted cashier. */
export type PackagesCheckoutResult =
  | { provider?: 'stripe'; url: string; session_id: string; purchase_id: string; mode: string }
  | {
      provider: 'ecpay'
      form_action: string
      form_fields: Record<string, string>
      purchase_id: string
      mode: string
    }

export interface CombinedCreateBody {
  booking_token: string
  guest_id?: string
  pet_name?: string
  species?: string
  breed?: string
  boarding?: {
    room_id?: string
    service_type: string
    check_in_date: string
    check_out_date: string
    size_tier?: string
    special_instructions?: string
    package_purchase_id?: string
    add_ons?: AddOnLine[]
  }
  grooming?: {
    service_id?: string
    package_id?: string
    groomer_id?: string
    scheduled_date: string
    scheduled_start: string
    scheduled_end: string
    size_tier?: string
    coat_type?: string
    special_requests?: string
    package_purchase_id?: string
    add_ons?: AddOnLine[]
  }
}

export const api = {
  getProfile: (slug: string): Promise<BusinessProfile> =>
    USE_MOCK ? mock.getProfile(slug) : request(`/${slug}`),

  getAvailability: (
    slug: string,
    p: { check_in: string; check_out: string; service_type?: string },
  ): Promise<RoomCard[]> =>
    USE_MOCK
      ? mock.getAvailability(p)
      : request(`/${slug}/availability${qs(p)}`),

  getGroomingServices: (slug: string): Promise<GroomingCatalog> =>
    USE_MOCK ? mock.getGroomingServices() : request(`/${slug}/grooming/services`),

  getGroomers: (slug: string): Promise<Groomer[]> =>
    USE_MOCK ? mock.getGroomers() : request(`/${slug}/grooming/groomers`),

  getGroomingSlots: (
    slug: string,
    p: { date: string; service_id?: string; package_id?: string; groomer_id?: string },
  ): Promise<GroomingSlot[]> =>
    USE_MOCK ? mock.getGroomingSlots(p) : request(`/${slug}/grooming/slots${qs(p)}`),

  pricingPreview: (
    slug: string,
    body: {
      room_id: string
      check_in_date: string
      check_out_date: string
      size_tier?: string
      service_type?: string
    },
  ): Promise<PricingPreview> =>
    USE_MOCK
      ? mock.pricingPreview(body)
      : request(`/${slug}/pricing-preview`, { method: 'POST', body: JSON.stringify(body) }),

  identityStart: (
    slug: string,
    body: { phone: string; name?: string; source: string },
  ): Promise<IdentityStartResponse> =>
    USE_MOCK
      ? mock.identityStart(body)
      : request(`/${slug}/identity/start`, { method: 'POST', body: JSON.stringify(body) }),

  identityVerify: (
    slug: string,
    body: { identity_ticket: string; code: string },
  ): Promise<IdentityVerifyResponse> =>
    USE_MOCK
      ? mock.identityVerify(body)
      : request(`/${slug}/identity/verify`, { method: 'POST', body: JSON.stringify(body) }),

  identityLiff: (
    slug: string,
    body: { line_id_token: string; phone: string; name?: string },
  ): Promise<IdentityVerifyResponse> =>
    USE_MOCK
      ? mock.identityLiff()
      : request(`/${slug}/identity/liff`, { method: 'POST', body: JSON.stringify(body) }),

  createReservation: (slug: string, body: BoardingCreateBody): Promise<BookingResult> =>
    USE_MOCK
      ? mock.createReservation(body)
      : request(`/${slug}/reservation`, { method: 'POST', body: JSON.stringify(body) }),

  createGrooming: (slug: string, body: GroomingCreateBody): Promise<BookingResult> =>
    USE_MOCK
      ? mock.createGrooming(body)
      : request(`/${slug}/grooming`, { method: 'POST', body: JSON.stringify(body) }),

  createCombined: (slug: string, body: CombinedCreateBody): Promise<MultiBookingResult> =>
    USE_MOCK
      ? mock.createCombined(body)
      : request(`/${slug}/booking`, { method: 'POST', body: JSON.stringify(body) }),

  // C3 — Packages browse + buy + redeem
  listPackagesCatalog: (
    slug: string,
    service_type?: 'boarding' | 'daycare' | 'grooming',
  ): Promise<{ clinic_id: string; packages: ServicePackageOffering[] }> =>
    USE_MOCK
      ? mock.listPackagesCatalog(service_type)
      : request(
          `/${slug}/packages/catalog${
            service_type ? `?service_type=${service_type}` : ''
          }`,
        ),

  // ADDONS-4 — per-item add-on catalog for a service type.
  listAddOns: (
    slug: string,
    service_type: 'boarding' | 'daycare' | 'grooming',
  ): Promise<AddOnOffering[]> =>
    USE_MOCK
      ? mock.listAddOns(service_type)
      : request(`/${slug}/addons?service_type=${service_type}`),

  createPackagesCheckout: (
    slug: string,
    body: PackageCheckoutBody,
  ): Promise<PackagesCheckoutResult> =>
    USE_MOCK
      ? mock.createPackagesCheckout(body)
      : request(`/${slug}/packages/checkout`, {
          method: 'POST',
          body: JSON.stringify(body),
        }),

  listMyPets: (
    slug: string,
    booking_token: string,
  ): Promise<{ pets: MyPetPublic[] }> =>
    USE_MOCK
      ? mock.listMyPets(booking_token)
      : request(`/${slug}/my-pets`, {
          method: 'POST',
          body: JSON.stringify({ booking_token }),
        }),

  reconcilePackagesCheckout: (
    slug: string,
    session_id: string,
  ): Promise<{ status: string; purchase: MyPackagePublic | null }> =>
    USE_MOCK
      ? mock.reconcilePackagesCheckout(session_id)
      : request(`/${slug}/packages/checkout/${session_id}/reconcile`),

  // ECPay rail's reconcile keys on MerchantTradeNo (the Stripe one keys on
  // session_id). Used by the post-payment return handler to confirm the
  // purchase settled after the browser redirects back from the cashier.
  reconcilePackagesEcpay: (
    slug: string,
    merchant_trade_no: string,
  ): Promise<{ status: string; purchase: MyPackagePublic | null }> =>
    USE_MOCK
      ? mock.reconcilePackagesCheckout(merchant_trade_no)
      : request(`/${slug}/packages/ecpay/${merchant_trade_no}/reconcile`),

  listMyPackages: (
    slug: string,
    booking_token: string,
    service_type?: 'boarding' | 'daycare' | 'grooming',
  ): Promise<{ purchases: MyPackagePublic[] }> =>
    USE_MOCK
      ? mock.listMyPackages(booking_token, service_type)
      : request(`/${slug}/my-packages`, {
          method: 'POST',
          body: JSON.stringify({ booking_token, service_type }),
        }),
}
